/**
 * PresetManagementPanel - Preset library card content
 */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Save, Download, Loader2, Lock, Crown, Trash2, Edit, Share2, Check, Link, FlaskConical, Brain, Leaf, Sparkles, BookOpen, ChevronDown, ChevronUp, List, ChevronsUpDown, GripVertical, ArrowUp, ArrowDown, Clock, Search, X } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import FirstVisitHint from './FirstVisitHint';
import { readRecent } from '../lib/presetRecency';

// localStorage keys — order is the user's preferred sequence of preset IDs
// (only IDs they've actively moved); view mode is the dropdown ↔ list
// toggle. The "recently used" cache is owned by lib/presetRecency.js so
// BrainwaveGenerator can write to it the moment the user hits Play.
const PRESET_ORDER_KEY = 'neurowave_preset_order';
const PRESET_VIEW_MODE_KEY = 'neurowave_preset_view_mode';

const readStoredOrder = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(PRESET_ORDER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

const writeStoredOrder = (order) => {
  try { localStorage.setItem(PRESET_ORDER_KEY, JSON.stringify(order)); } catch { /* ignore */ }
};

// Apply the user's saved order to the live preset list. IDs in the stored
// order that no longer exist are dropped; presets not yet in the stored
// order are appended at the end so newly-installed presets aren't hidden.
const applyStoredOrder = (presets, storedOrder) => {
  if (!storedOrder.length) return presets;
  const byId = new Map(presets.map((p) => [p.id, p]));
  const seen = new Set();
  const ordered = [];
  for (const id of storedOrder) {
    const preset = byId.get(id);
    if (preset && !seen.has(id)) {
      ordered.push(preset);
      seen.add(id);
    }
  }
  for (const preset of presets) {
    if (!seen.has(preset.id)) ordered.push(preset);
  }
  return ordered;
};

const SCIENCE_TIERS = {
  evidence_based: {
    label: 'Evidence-Based',
    icon: FlaskConical,
    className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    blurb: 'Peer-reviewed research support',
  },
  applied_neuroscience: {
    label: 'Applied Neuroscience',
    icon: Brain,
    className: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
    blurb: 'Established clinical / BCI practice',
  },
  traditional: {
    label: 'Traditional',
    icon: Leaf,
    className: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    blurb: 'Historical / spiritual — not medical',
  },
  experimental: {
    label: 'Experimental',
    icon: Sparkles,
    className: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30',
    blurb: 'Emerging — user-reported effects',
  },
};

const ScienceTierBadge = ({ tier }) => {
  const meta = SCIENCE_TIERS[tier];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      title={meta.blurb}
      className={`inline-flex items-center gap-1 border rounded-full px-2 py-0.5 text-[10px] font-medium ${meta.className}`}
      data-testid={`science-tier-${tier}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const PresetManagementPanel = memo(({
  presets,
  currentPreset,
  presetName,
  onPresetNameChange,
  loading,
  canSavePresets,
  canExportAudio,
  canDeletePreset,
  onSavePreset,
  onLoadPreset,
  onEditPreset,
  onDeletePreset,
  onExportClick,
  onSharePreset,
  shareLoading,
  copiedShareId,
}) => {
  const { theme } = useTheme();
  const [researchOpen, setResearchOpen] = useState(false);

  // View mode (list vs dropdown). Persisted in localStorage. Default: list —
  // the scrollable inline library makes better use of the card space; users
  // who explicitly switched to the dropdown keep their choice.
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'list';
    try {
      return localStorage.getItem(PRESET_VIEW_MODE_KEY) === 'dropdown' ? 'dropdown' : 'list';
    } catch { return 'list'; }
  });
  const toggleViewMode = useCallback(() => {
    setViewMode((m) => {
      const next = m === 'list' ? 'dropdown' : 'list';
      try { localStorage.setItem(PRESET_VIEW_MODE_KEY, next); } catch { /* ignore */ }
      return next;
    });
  }, []);

  // User's custom preset order (array of IDs). Initialised from localStorage,
  // then mutated by the drag-reorder / arrow-button handlers below.
  const [storedOrder, setStoredOrder] = useState(readStoredOrder);
  const orderedPresets = useMemo(() => applyStoredOrder(presets, storedOrder), [presets, storedOrder]);

  // Search query — filters both views. Drag-reorder is disabled while a
  // filter is active because row indices no longer map to the stored order.
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return null;
    return orderedPresets.filter((p) =>
      p.name?.toLowerCase().includes(q) ||
      p.beat_type?.toLowerCase().includes(q) ||
      String(p.base_frequency ?? '').includes(q) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [q, orderedPresets]);

  // Recently-used preset IDs (most-recent first, max RECENT_MAX). Written
  // by BrainwaveGenerator the moment the user hits Play with a preset
  // loaded; we just read + react to changes here. The `storage` event
  // fires for cross-tab updates; the custom event fills the same-tab gap.
  const [recentIds, setRecentIds] = useState(readRecent);
  useEffect(() => {
    const refresh = () => setRecentIds(readRecent());
    window.addEventListener('storage', refresh);
    window.addEventListener('neurowave:preset-recent-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('neurowave:preset-recent-updated', refresh);
    };
  }, []);
  const recentPresets = useMemo(() => {
    if (!recentIds.length) return [];
    const byId = new Map(presets.map((p) => [p.id, p]));
    return recentIds.map((id) => byId.get(id)).filter(Boolean);
  }, [recentIds, presets]);
  const recentIdSet = useMemo(() => new Set(recentPresets.map((p) => p.id)), [recentPresets]);
  // Presets remaining in the main reorder area — recently-used entries are
  // hoisted into the pinned section so we hide them here to avoid duplicates.
  const reorderablePresets = useMemo(
    () => orderedPresets.filter((p) => !recentIdSet.has(p.id)),
    [orderedPresets, recentIdSet],
  );

  // Index of the row currently being dragged (HTML5 drag-and-drop on
  // desktop). Used to highlight the source and decide where to insert on
  // dragover. Stays null when nothing is being dragged.
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Persist a new order — given the canonical array of presets in their
  // intended sequence, store just their IDs.
  const persistOrder = useCallback((newSequence) => {
    const ids = newSequence.map((p) => p.id);
    setStoredOrder(ids);
    writeStoredOrder(ids);
  }, []);

  // Move a preset within the *reorderable* subset (non-recents). The pinned
  // "Recently used" rows aren't user-reorderable — they're sorted by usage
  // recency. After a reorder we rebuild the full canonical order as
  // [recents..., reorderable...] and persist; once a preset ages out of
  // recents it falls back into the reorderable list at the top.
  const reorder = useCallback((fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    if (fromIdx >= reorderablePresets.length || toIdx >= reorderablePresets.length) return;
    const next = reorderablePresets.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    persistOrder([...recentPresets, ...next]);
  }, [reorderablePresets, recentPresets, persistOrder]);

  // Drag handlers — kept inline rather than extracted because they all
  // share the dragIndex/dragOverIndex state and would otherwise need a
  // useReducer to thread it through. Native HTML5 DnD only; touch users
  // fall back to the visible arrow buttons.
  const onRowDragStart = (e, idx) => {
    setDragIndex(idx);
    // Required for Firefox to actually start the drag operation.
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* ignore */ }
    e.dataTransfer.effectAllowed = 'move';
  };
  const onRowDragOver = (e, idx) => {
    if (dragIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIndex) setDragOverIndex(idx);
  };
  const onRowDrop = (e, idx) => {
    e.preventDefault();
    if (dragIndex !== null) reorder(dragIndex, idx);
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const onRowDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Stale IDs in `storedOrder` (e.g., a preset deleted server-side) are
  // already filtered at render time inside applyStoredOrder, so we don't
  // need a separate cleanup effect. The tiny localStorage drift self-heals
  // the next time the user reorders anything.

  const selectedPreset = presets.find(p => p.id === currentPreset);
  const customCount = presets.filter(canDeletePreset).length;
  const builtInCount = presets.filter(p => !canDeletePreset(p)).length;

  return (
    <div className="space-y-4" data-testid="preset-management-panel">
      <div className="flex gap-2">
        <Input
          placeholder="Preset name"
          value={presetName}
          onChange={(e) => onPresetNameChange(e.target.value)}
          className={theme.inputBg + " placeholder:text-white/60"}
          disabled={!canSavePresets}
          data-testid="preset-name-input"
        />
        <Button
          onClick={onSavePreset}
          disabled={loading || !canSavePresets}
          className={`${canSavePresets ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-600 cursor-not-allowed'} relative`}
          data-testid="save-preset-btn"
        >
          {!canSavePresets && <Lock className="w-4 h-4 absolute -top-1 -right-1 text-yellow-400" />}
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        </Button>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-white text-sm">Preset Library</label>
          <div className="flex items-center gap-2">
            <span className={`${theme.dimText} text-xs`}>{presets.length} presets</span>
            <button
              type="button"
              onClick={toggleViewMode}
              title={viewMode === 'list' ? 'Switch to dropdown view' : 'Switch to list view'}
              className="h-7 w-7 rounded-md flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5 transition-colors"
              data-testid="preset-view-toggle"
              aria-label={viewMode === 'list' ? 'Switch to dropdown view' : 'Switch to list view'}
            >
              {viewMode === 'list'
                ? <ChevronsUpDown className="w-4 h-4" />
                : <List className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Search — filters both views */}
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search presets..."
            className={`${theme.inputBg} placeholder:text-white/40 pl-8 pr-8 h-8 text-sm`}
            data-testid="preset-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded text-white/40 hover:text-white transition-colors"
              data-testid="preset-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* First-visit hint — only in list mode, only on first visit. */}
        {viewMode === 'list' && (
          <FirstVisitHint
            storageKey="neurowave_preset_reorder_hint"
            icon={<GripVertical className="w-3.5 h-3.5" />}
            data-testid="preset-reorder-hint"
          >
            drag a row to reorder · use{' '}
            <ArrowUp className="w-3 h-3 inline -mt-0.5" /> /{' '}
            <ArrowDown className="w-3 h-3 inline -mt-0.5" /> on mobile
          </FirstVisitHint>
        )}

        {/* Dropdown view — original picker, default for non-opted-in users. */}
        {viewMode === 'dropdown' && (
        <div className="space-y-2">
          <Select onValueChange={onLoadPreset} value={currentPreset || ""}>
            <SelectTrigger className={theme.inputBg + " hover:bg-white/5"} data-testid="preset-select-trigger">
              <SelectValue placeholder="Select a preset..." />
            </SelectTrigger>
            <SelectContent className={theme.selectBg + " max-h-80"}>
              {presets.length === 0 ? (
                <SelectItem value="no-presets" disabled className="text-white/60">
                  No presets available
                </SelectItem>
              ) : (
                (searchResults || orderedPresets).map(preset => (
                  <SelectItem
                    key={preset.id}
                    value={preset.id}
                    className="text-white hover:bg-white/10 focus:bg-white/10 cursor-pointer"
                  >
                    {preset.name} &bull; {preset.beat_type} &bull; {preset.base_frequency}Hz
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        )}

        {/* List view — draggable rows + arrow-button fallback. */}
        {viewMode === 'list' && (
        <div
          className="rounded-lg border border-white/10 bg-white/[0.02] max-h-96 overflow-y-auto divide-y divide-white/5"
          data-testid="preset-list-view"
        >
          {/* Pinned "Recently used" section — hidden while searching. */}
          {!q && recentPresets.length > 0 && (
            <div data-testid="preset-recent-section">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-white/40 font-medium flex items-center gap-1.5 bg-white/[0.015]">
                <Clock className="w-3 h-3" />
                Recently used
              </div>
              {recentPresets.map((preset) => {
                const isActive = preset.id === currentPreset;
                return (
                  <div
                    key={`recent-${preset.id}`}
                    onClick={() => onLoadPreset(preset.id)}
                    className={`flex items-center gap-2 px-2 py-2 cursor-pointer select-none transition-colors ${
                      isActive ? 'bg-blue-500/15 hover:bg-blue-500/20' : 'hover:bg-white/[0.04]'
                    }`}
                    data-testid={`preset-recent-row-${preset.id}`}
                  >
                    {/* Spacer matching the grip-handle width below so names align */}
                    <span className="w-4 shrink-0" aria-hidden />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${isActive ? 'text-white font-medium' : 'text-white/85'}`}>{preset.name}</div>
                      <div className="text-[10px] text-white/40 truncate">
                        {preset.beat_type} · {preset.base_frequency}Hz · {Math.round((preset.duration || 0) / 60)}min
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Header for the rest of the library — only shown when both
                  sections are populated, otherwise it's just noise. */}
              {reorderablePresets.length > 0 && (
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-white/40 font-medium bg-white/[0.015]">
                  All presets
                </div>
              )}
            </div>
          )}
          {(q ? searchResults.length === 0 : reorderablePresets.length === 0 && recentPresets.length === 0) ? (
            <div className="px-3 py-4 text-center text-white/40 text-xs" data-testid="preset-list-empty">
              {q ? `No presets match "${query.trim()}"` : 'No presets available'}
            </div>
          ) : (q ? searchResults : reorderablePresets).map((preset, idx) => {
            const isActive = preset.id === currentPreset;
            const isDragging = dragIndex === idx;
            const isDragTarget = dragOverIndex === idx && dragIndex !== null && dragIndex !== idx;
            return (
              <div
                key={preset.id}
                draggable={!q}
                onDragStart={(e) => onRowDragStart(e, idx)}
                onDragOver={(e) => onRowDragOver(e, idx)}
                onDrop={(e) => onRowDrop(e, idx)}
                onDragEnd={onRowDragEnd}
                onClick={() => onLoadPreset(preset.id)}
                className={`flex items-center gap-2 px-2 py-2 cursor-pointer select-none transition-colors ${
                  isActive ? 'bg-blue-500/15 hover:bg-blue-500/20' : 'hover:bg-white/[0.04]'
                } ${isDragging ? 'opacity-40' : ''} ${isDragTarget ? 'ring-2 ring-blue-400/60 ring-inset' : ''}`}
                data-testid={`preset-row-${preset.id}`}
              >
                {q
                  ? <span className="w-4 shrink-0" aria-hidden />
                  : <GripVertical className="w-4 h-4 text-white/30 shrink-0 cursor-grab active:cursor-grabbing" aria-hidden />}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm truncate ${isActive ? 'text-white font-medium' : 'text-white/85'}`}>{preset.name}</div>
                  <div className="text-[10px] text-white/40 truncate">
                    {preset.beat_type} · {preset.base_frequency}Hz · {Math.round((preset.duration || 0) / 60)}min
                  </div>
                </div>
                {!q && (
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); reorder(idx, idx - 1); }}
                    disabled={idx === 0}
                    className="h-7 w-7 rounded flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:hover:bg-transparent transition-colors"
                    title="Move up"
                    data-testid={`preset-move-up-${preset.id}`}
                    aria-label={`Move ${preset.name} up`}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); reorder(idx, idx + 1); }}
                    disabled={idx === reorderablePresets.length - 1}
                    className="h-7 w-7 rounded flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:hover:bg-transparent transition-colors"
                    title="Move down"
                    data-testid={`preset-move-down-${preset.id}`}
                    aria-label={`Move ${preset.name} down`}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                )}
              </div>
            );
          })}
        </div>
        )}

        <div className="space-y-2">
          {/* Current Preset Info */}
          {selectedPreset && (
            <div className={`${theme.infoBg} border rounded-lg p-3`}>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-white text-sm font-medium">{selectedPreset.name}</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onSharePreset(selectedPreset)}
                      disabled={shareLoading}
                      className={`h-8 w-8 p-0 ${copiedShareId === selectedPreset.id ? 'text-green-400' : 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20'}`}
                      title={copiedShareId === selectedPreset.id ? "Link copied!" : "Share preset"}
                      data-testid="share-preset-btn"
                    >
                      {copiedShareId === selectedPreset.id ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                    </Button>
                    {canDeletePreset(selectedPreset) && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditPreset(selectedPreset)}
                          className="h-8 w-8 p-0 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300"
                          title="Edit preset"
                          data-testid="edit-preset-btn"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeletePreset(selectedPreset)}
                          className="h-8 w-8 p-0 hover:bg-red-500/20 text-red-400 hover:text-red-300"
                          title="Delete preset"
                          data-testid="delete-preset-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className={`${theme.mutedText} text-xs`}>
                  {selectedPreset.description || 'No description available'}
                </div>
                <div className={`${theme.dimText} text-xs`}>
                  {selectedPreset.beat_type} &bull; {selectedPreset.base_frequency}Hz &bull; {Math.round(selectedPreset.duration / 60)}min
                  {selectedPreset.min_session_minutes && (
                    <> &bull; <span className="text-white/60">min {selectedPreset.min_session_minutes}m for best effect</span></>
                  )}
                </div>

                {/* Scientific integrity: tier badge + research expander */}
                {selectedPreset.science_tier && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <ScienceTierBadge tier={selectedPreset.science_tier} />
                    {selectedPreset.best_time && (
                      <span className="text-[10px] text-white/40">Best: {selectedPreset.best_time}</span>
                    )}
                  </div>
                )}

                {selectedPreset.research_citation && (
                  <div className="mt-1">
                    <button
                      type="button"
                      onClick={() => setResearchOpen(v => !v)}
                      className="flex items-center gap-1 text-[11px] text-purple-300 hover:text-purple-200 underline-offset-2 hover:underline"
                      data-testid="toggle-research-citation"
                    >
                      <BookOpen className="w-3 h-3" />
                      {researchOpen ? 'Hide research note' : 'Research & context'}
                      {researchOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    {researchOpen && (
                      <div className="mt-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/5 text-[11px] leading-relaxed text-white/65">
                        {selectedPreset.research_citation}
                        {selectedPreset.research_url && (
                          <>
                            {' '}
                            <a
                              href={selectedPreset.research_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-purple-300 hover:text-purple-200 underline"
                              data-testid="research-citation-link"
                            >
                              View source →
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {selectedPreset.tags && selectedPreset.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedPreset.tags.map(tag => (
                      <span key={tag} className={`inline-block ${theme.tagBg} px-2 py-0.5 rounded text-xs`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Preset Management Info */}
        <div className={`${theme.dimText} text-xs flex justify-between`}>
          <span>{customCount} custom preset(s)</span>
          <span>{builtInCount} built-in</span>
        </div>
      </div>

      <Button
        onClick={onExportClick}
        disabled={loading || !canExportAudio}
        className={`w-full relative ${canExportAudio ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-600 cursor-not-allowed'}`}
        data-testid="export-audio-btn"
      >
        {!canExportAudio && <Lock className="w-4 h-4 absolute -top-1 -right-1 text-yellow-400" />}
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Exporting...
          </>
        ) : (
          <>
            <Download className="w-4 h-4 mr-2" />
            Export Audio
            {!canExportAudio && <Crown className="w-4 h-4 ml-2 text-yellow-400" />}
          </>
        )}
      </Button>
    </div>
  );
});

PresetManagementPanel.displayName = 'PresetManagementPanel';

export default PresetManagementPanel;
